import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Search, Plus, Trash2, Check, Camera, X, MessageCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import * as crmBackend from '../services/crmBackendService';
import { compressImage } from '../lib/imageCompress';
import { stripHtml } from '../lib/utils';
import axios from 'axios';
import { loadDraft, saveDraft, clearDraft, hasContent, isPending, newClientId } from '../lib/unsavedDraft';
import { enqueue, runOp, errMessage, photoIdFromUrl } from '../lib/itemSync';
import AppVersion from '../components/AppVersion';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');


const SavedBadge = ({ row, onRetry }) => {
  if (row._sync === 'error') {
    return (
      <span className="text-[10px] font-semibold text-red-600 flex items-center gap-1" title={row._err}>
        Not saved
        <button type="button" onClick={onRetry} className="underline">Retry</button>
      </span>
    );
  }
  if (row._sync === 'saved') return <span className="text-[10px] font-semibold text-green-700">Saved ✓</span>;
  return <span className="text-[10px] font-semibold text-amber-600">Saving…</span>;
};

const PhotoSlot = ({ row, type, isSigned, uploading, onPick, onRemove }) => {
  const url = type === 'before' ? row.custom_before_photo_url : row.custom_after_photo_url;
  const id = photoIdFromUrl(url);
  const pending = row._photos?.[type];
  const border = type === 'before' ? 'border-green-400' : 'border-blue-400';
  const src = pending || (id ? `${API_BASE}/api/photos/${id}/view` : null);
  return (
    <div className="flex flex-col items-center">
      <span className="text-[9px] text-gray-400 mb-0.5">{type === 'before' ? 'Before' : 'After'}</span>
      {isSigned ? (
        src ? (
          <a href={src} target="_blank" rel="noopener noreferrer">
            <img src={src} alt={type} className={`w-8 h-8 rounded object-cover border ${border} cursor-zoom-in`} />
          </a>
        ) : (
          <div className="w-8 h-8 rounded border border-gray-200 flex items-center justify-center text-gray-300 bg-gray-50"><Camera className="w-3.5 h-3.5" /></div>
        )
      ) : (
        <button onClick={onPick} disabled={uploading} title={src ? 'Replace photo' : 'Add photo'} className="shrink-0">
          {uploading ? (
            <div className="w-8 h-8 rounded border border-gray-200 flex items-center justify-center bg-gray-50">
              <div className={`w-3 h-3 border-2 ${type === 'before' ? 'border-green-400' : 'border-blue-400'} border-t-transparent rounded-full animate-spin`} />
            </div>
          ) : src ? (
            <img src={src} alt={type} className={`w-8 h-8 rounded object-cover border ${border}`} />
          ) : (
            <div className="w-8 h-8 rounded border border-dashed border-gray-300 flex items-center justify-center text-gray-400 bg-gray-50 hover:bg-gray-100"><Camera className="w-3.5 h-3.5" /></div>
          )}
        </button>
      )}
      {!isSigned && id && !pending && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="text-[10px] text-red-500 hover:text-red-700 mt-1 flex items-center justify-center gap-0.5">
          <Trash2 className="w-2.5 h-2.5" /> Remove
        </button>
      )}
    </div>
  );
};

const TechAddItems = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { toast } = useToast();
  const photoInputRef = useRef(null);
  const afterPhotoInputRef = useRef(null);
  const linePhotoInputRef = useRef(null);
  const loadFailedRef = useRef(true); // true until the server list has loaded; blocks draft writes
  const [restoredBanner, setRestoredBanner] = useState(null);

  // Every line is a "row" with client-only fields (prefixed "_"):
  //   _cid key, _sync 'saved'|'pending'|'saving'|'error', _err, _ver/_savedVer edit counters,
  //   _removed (delete queued), _putBase (server count before an in-flight add PUT), _photos (not yet uploaded)
  // Every change is persisted to ERP immediately through the serialized queue in lib/itemSync.
  const itemsRef = useRef([]);
  const formRef = useRef({});
  const timersRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [isSigned, setIsSigned] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);

  const [switchingItemIdx, setSwitchingItemIdx] = useState(null);
  const [subSearchQuery, setSubSearchQuery] = useState('');
  const [subSearchResults, setSubSearchResults] = useState([]);
  const [subSearching, setSubSearching] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [zone, setZone] = useState('');
  const [rate, setRate] = useState('');
  const [qty, setQty] = useState('1');
  const [description, setDescription] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [afterPhotoPreview, setAfterPhotoPreview] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatText, setChatText] = useState('');
  const [chatSending, setChatSending] = useState(false);

  formRef.current = { selectedItem, zone, rate, qty, description, photoPreview, afterPhotoPreview };

  // Durable safety net: everything not yet verified on the server, plus the form.
  const persist = () => {
    if (loadFailedRef.current) return;
    const d = { ...formRef.current, unsavedItems: itemsRef.current.filter(isPending) };
    if (!hasContent(d)) { clearDraft(jobId); return; }
    saveDraft(jobId, d);
  };
  const updateItems = (fn) => {
    const next = fn(itemsRef.current);
    itemsRef.current = next;
    setItems(next);
    persist();
  };
  const patchRow = (cid, patch) => updateItems(rows => rows.map(r => (r._cid === cid ? { ...r, ...patch } : r)));
  const getRow = (cid) => itemsRef.current.find(r => r._cid === cid);

  // Queue a save for one row. The operation (add / edit / remove) is decided
  // when its turn comes, from the row's latest state.
  const syncRow = (cid) => {
    clearTimeout(timersRef.current[cid]);
    patchRow(cid, { _sync: 'saving', _err: '' });
    let ver;
    return enqueue(jobId, async () => {
      const row = getRow(cid);
      if (!row) return { skipped: true };
      let op;
      if (row._removed) op = row.name ? 'remove' : null;
      else op = row.name ? 'edit' : 'add';
      if (!op) return { skipped: true, drop: true };
      if (op === 'edit' && row._savedVer === row._ver && !row._photos) return { skipped: true, done: true };
      ver = row._ver;
      const res = await runOp({
        api: crmBackend, jobId, op, stripHtml, getRow: () => getRow(cid),
        hooks: {
          techName: localStorage.getItem('technicianName') || '',
          beforePut: ({ putBase, putNames, patch }) => patchRow(cid, { ...(putBase != null ? { _putBase: putBase } : {}), ...(putNames ? { _putNames: putNames } : {}), ...(patch || {}), _photos: null }),
        },
      });
      return { ...res, op };
    }).then(res => {
      if (res.drop || (res.op === 'remove' && !res.skipped) ) { updateItems(rows => rows.filter(r => r._cid !== cid)); return; }
      if (res.skipped && !res.done) { return; }
      const row = getRow(cid);
      if (!row) return;
      const stale = ver !== undefined && row._ver !== ver;
      patchRow(cid, {
        ...(res.row?.name ? { name: res.row.name } : {}),
        _putBase: undefined,
        _putNames: undefined,
        _savedVer: ver !== undefined ? ver : row._savedVer,
        _sync: stale ? 'pending' : 'saved', _err: '',
      });
      // Server auto-sorts rows by zone on save: mirror that order locally for
      // rows it already knows (unsaved/pending rows stay after them).
      if (Array.isArray(res.rows) && res.rows.length) {
        const pos = new Map(res.rows.map((r, i) => [r.name, i]));
        updateItems(rows => [
          ...rows.filter(r => r.name && pos.has(r.name)).sort((a, b) => pos.get(a.name) - pos.get(b.name)),
          ...rows.filter(r => !(r.name && pos.has(r.name))),
        ]);
      }
    }).catch(err => {
      const msg = errMessage(err);
      patchRow(cid, { _sync: 'error', _err: msg });
      toast({ title: 'Item NOT saved to job yet', description: `${msg} — it is kept on this device. Tap Retry.`, variant: 'destructive' });
    });
  };

  const editRow = (cid, patch, immediate = false) => {
    updateItems(rows => rows.map(r => {
      if (r._cid !== cid) return r;
      const n = { ...r, ...patch, _ver: (r._ver || 0) + 1, _sync: 'pending', _err: '' };
      n.amount = (Number(n.qty) || 0) * (Number(n.rate) || 0);
      return n;
    }));
    clearTimeout(timersRef.current[cid]);
    if (immediate) syncRow(cid);
    else timersRef.current[cid] = setTimeout(() => syncRow(cid), 700);
  };

  const flushAll = () => {
    Object.keys(timersRef.current).forEach(cid => {
      if (timersRef.current[cid]) { clearTimeout(timersRef.current[cid]); timersRef.current[cid] = null; if (getRow(cid)) syncRow(cid); }
    });
  };

  const retryAll = () => itemsRef.current.filter(r => r._sync === 'error').forEach(r => syncRow(r._cid));

  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flushAll(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flushAll);
    return () => { document.removeEventListener('visibilitychange', onHide); window.removeEventListener('pagehide', flushAll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    crmBackend.getEstimateDetail(jobId)
      .then(result => {
        const doc = result.data || {};
        setIsSigned(doc.docstatus === 1 || doc.custom_signature_captured === 1 || doc.custom_approval_status === 'Approved');
        let rows = (doc.items || []).map(i => ({
          _cid: `s${i.name}`, _sync: 'saved', _ver: 0, _savedVer: 0,
          name: i.name, item_code: i.item_code, item_name: i.item_name, qty: i.qty, rate: i.rate, amount: i.amount,
          description: stripHtml(i.description),
          custom_zone: i.custom_zone || '',
          custom_before_photo_url: i.custom_before_photo_url || '',
          custom_after_photo_url: i.custom_after_photo_url || '',
        }));
        // Re-apply anything the device still had pending (reload / app kill mid-save).
        const draft = loadDraft(jobId);
        const pendingDraft = (draft?.unsavedItems || []).map(u => ({ ...u, _cid: u._cid || newClientId() }));
        const toResync = [];
        pendingDraft.forEach(d => {
          if (d.name) {
            const idx = rows.findIndex(r => r.name === d.name);
            if (idx < 0) return;
            rows[idx] = { ...rows[idx], ...d, _sync: 'pending', _err: '', _ver: 1, _savedVer: 0 };
            toResync.push(rows[idx]._cid);
          } else {
            rows.push({ ...d, _sync: 'pending', _err: '', _ver: 1, _savedVer: 0 });
            toResync.push(d._cid);
          }
        });
        itemsRef.current = rows;
        setItems(rows);
        loadFailedRef.current = false;
        if (draft && hasContent(draft)) {
          if (draft.selectedItem) setSelectedItem(draft.selectedItem);
          if (draft.zone) setZone(draft.zone);
          if (draft.rate) setRate(draft.rate);
          if (draft.qty) setQty(draft.qty);
          if (draft.description) setDescription(draft.description);
          if (draft.photoPreview) setPhotoPreview(draft.photoPreview);
          if (draft.afterPhotoPreview) setAfterPhotoPreview(draft.afterPhotoPreview);
          setRestoredBanner({ count: toResync.length, hadDraft: !!draft.selectedItem, photosDropped: !!draft.photosDropped });
        }
        toResync.forEach(cid => syncRow(cid));
      })
      .catch(err => { toast({ title: 'Could not load job', description: err.message, variant: 'destructive' }); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API_BASE}/api/resource/Item`, {
          params: {
            fields: JSON.stringify(['name', 'item_name', 'item_group', 'stock_uom', 'description']),
            filters: JSON.stringify([['disabled', '=', 0], ['item_name', 'like', `%${q}%`]]),
            limit_page_length: 15,
            order_by: 'item_name asc',
          }
        });
        setSearchResults(data?.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const q = subSearchQuery.trim();
    if (q.length < 2) { setSubSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSubSearching(true);
      try {
        const { data } = await axios.get(`${API_BASE}/api/resource/Item`, {
          params: {
            fields: JSON.stringify(['name', 'item_name', 'item_group', 'stock_uom', 'description']),
            filters: JSON.stringify([['disabled', '=', 0], ['item_name', 'like', `%${q}%`]]),
            limit_page_length: 15,
            order_by: 'item_name asc',
          }
        });
        setSubSearchResults(data?.data || []);
      } catch {
        setSubSearchResults([]);
      } finally {
        setSubSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [subSearchQuery]);

  // Keep the in-progress form (not yet added) in the draft too.
  useEffect(() => { if (!loading) persist(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, selectedItem, zone, rate, qty, description, photoPreview, afterPhotoPreview]);

  const unsaved = items.filter(r => r._sync !== 'saved');
  const errorCount = items.filter(r => r._sync === 'error').length;
  const dirty = unsaved.length > 0 || !!selectedItem;
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const selectItem = async (item) => {
    setSelectedItem(item);
    setZone('');
    setRate('');
    setQty('1');
    setDescription(stripHtml(item.description));
    setPhotoPreview(null);
    setSearchQuery('');
    setSearchResults([]);
    // Auto-fill rate from ERPNext selling price
    try {
      const { data } = await axios.get(`${API_BASE}/api/resource/Item Price`, {
        params: {
          fields: JSON.stringify(['price_list_rate']),
          filters: JSON.stringify([['item_code', '=', item.name], ['selling', '=', 1]]),
          order_by: 'price_list_rate desc',
          limit_page_length: 1,
        },
      });
      const priceEntry = data?.data?.[0];
      if (priceEntry?.price_list_rate) {
        setRate(String(priceEntry.price_list_rate));
      }
    } catch {
      // leave rate empty — tech can enter manually
    }
  };

  const capturePhoto = async (file, setPreview) => {
    if (!file) return;
    try {
      const { dataUrl } = await compressImage(file);
      setPreview(dataUrl);
    } catch (err) {
      console.error('Photo compression failed, falling back to uncompressed', err);
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    capturePhoto(file, setPhotoPreview);
    e.target.value = '';
  };

  const openPhotoFor = (cid, type) => {
    setEditingPhoto({ cid, type });
    linePhotoInputRef.current?.click();
  };

  const handleLinePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    const target = editingPhoto;
    e.target.value = '';
    setEditingPhoto(null);
    if (!file || !target) return;
    setUploadingKey(`${target.cid}:${target.type}`);
    try {
      let dataUrl;
      try {
        ({ dataUrl } = await compressImage(file));
      } catch (err) {
        console.error('Photo compression failed, falling back to uncompressed', err);
        dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      const row = getRow(target.cid);
      if (!row) return;
      editRow(target.cid, { _photos: { ...(row._photos || {}), [target.type]: dataUrl } }, true);
    } catch (err) {
      toast({ title: 'Photo failed', description: errMessage(err), variant: 'destructive' });
    } finally {
      setUploadingKey(null);
    }
  };

  const addItem = () => {
    if (!selectedItem) return;
    const rateVal = parseFloat(rate) || 0;
    const qtyVal = parseFloat(qty) || 1;
    const cid = newClientId();
    const photos = {};
    if (photoPreview) photos.before = photoPreview;
    if (afterPhotoPreview) photos.after = afterPhotoPreview;
    // Added and written to ERP right now; the local draft is the safety net until verified.
    updateItems(prev => [...prev, {
      _cid: cid, _sync: 'pending', _ver: 1, _savedVer: 0,
      _photos: Object.keys(photos).length ? photos : null,
      item_code: selectedItem.name,
      item_name: selectedItem.item_name,
      qty: qtyVal,
      rate: rateVal,
      amount: rateVal * qtyVal,
      description: description,
      custom_zone: zone,
      custom_before_photo_url: '',
      custom_after_photo_url: '',
    }]);
    setSelectedItem(null);
    setZone('');
    setRate('');
    setQty('1');
    setDescription('');
    setPhotoPreview(null);
    setAfterPhotoPreview(null);
    syncRow(cid);
  };

  const removeItem = (cid) => {
    clearTimeout(timersRef.current[cid]);
    updateItems(prev => prev.map(r => (r._cid === cid ? { ...r, _removed: true, _sync: 'pending', _err: '' } : r)));
    syncRow(cid);
  };

  const switchItemForLine = async (cid, newItem) => {
    let newRate = '';
    try {
      const { data } = await axios.get(`${API_BASE}/api/resource/Item Price`, {
        params: {
          fields: JSON.stringify(['price_list_rate']),
          filters: JSON.stringify([['item_code', '=', newItem.name], ['selling', '=', 1]]),
          order_by: 'price_list_rate desc',
          limit_page_length: 1,
        },
      });
      const priceEntry = data?.data?.[0];
      if (priceEntry?.price_list_rate) newRate = priceEntry.price_list_rate;
    } catch {}
    const row = getRow(cid);
    if (row) {
      editRow(cid, {
        item_code: newItem.name, item_name: newItem.item_name,
        rate: newRate || row.rate, description: stripHtml(newItem.description),
      }, true);
    }
    setSwitchingItemIdx(null);
    setSubSearchQuery('');
    setSubSearchResults([]);
  };

  const handleRemovePhoto = async (cid, type) => {
    const row = getRow(cid);
    const photoId = photoIdFromUrl(type === 'before' ? row?.custom_before_photo_url : row?.custom_after_photo_url);
    if (!photoId) return;
    try {
      await crmBackend.deletePhoto(photoId);
      editRow(cid, type === 'before' ? { custom_before_photo_url: '' } : { custom_after_photo_url: '' }, true);
      toast({ title: 'Photo deleted' });
    } catch (err) {
      toast({ title: 'Failed to delete photo', description: err.message, variant: 'destructive' });
    }
  };

  // Reachable from both the tech app and the admin CRM — send each session back
  // to where it actually has access, not the tech-only job detail page.
  const isTechSession = localStorage.getItem('techAuthenticated');
  const backPath = isTechSession ? `/tech/job/${jobId}` : `/dashboard`;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Loading…</p>
    </div>
  );

  const visible = items.filter(r => !r._removed);
  const total = visible.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 pb-[calc(6rem+env(safe-area-inset-bottom,0px))]">
      <div className="bg-green-700 text-white sticky top-0 z-50 shadow-lg">
        <div className="px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(backPath)}
            className="flex items-center space-x-2 hover:bg-green-600 px-3 py-2 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
          <span className="font-bold">Add Items</span>
          <button
            onClick={() => { setChatText(''); setChatOpen(true); }}
            className="flex items-center space-x-1 hover:bg-green-600 px-3 py-2 rounded-lg transition-colors text-sm"
          >
            <MessageCircle className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {restoredBanner && (
          <div className="bg-blue-50 border border-blue-300 rounded-lg p-3 text-blue-900 text-sm flex items-start justify-between gap-2">
            <span>
              Restored {restoredBanner.count} unsaved change(s){restoredBanner.hadDraft ? ' + item in progress' : ''} — saving them to the job now.
              {restoredBanner.photosDropped ? ' (In-progress photo was too large to keep; retake it.)' : ''}
            </span>
            <button onClick={() => setRestoredBanner(null)} aria-label="Dismiss"><X className="w-4 h-4" /></button>
          </div>
        )}
        {isSigned && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm font-semibold flex items-center justify-center gap-2 shadow-sm">
            <span>⚠️ Estimate is approved/signed. Items and photos are locked.</span>
          </div>
        )}

        {!isSigned && (
          <Card>
            <CardHeader><CardTitle className="text-base">Search Items</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                className="pl-9 h-11"
                placeholder="Type item name to search…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            {searching && <p className="text-sm text-gray-500">Searching…</p>}
            {searchResults.length > 0 && (
              <div className="border rounded-md overflow-hidden">
                {searchResults.map(item => (
                  <button key={item.name} onClick={() => selectItem(item)}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-green-50 border-b last:border-0">
                    <p className="font-medium text-gray-900">{item.item_name}</p>
                    <p className="text-xs text-gray-500">{item.item_group} · {item.name}</p>
                  </button>
                ))}
              </div>
            )}

            {selectedItem && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 space-y-3">
                <p className="font-semibold text-green-900">{selectedItem.item_name}</p>

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Zone</label>
                  <Input
                    type="text"
                    placeholder="e.g. Zone 3, Front Yard"
                    value={zone}
                    onChange={e => setZone(e.target.value)}
                    className="h-10"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Rate ($)</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={rate}
                    onChange={e => setRate(e.target.value)}
                    className="h-10 bg-white"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Qty</label>
                  <Input type="number" min="0.01" step="any" value={qty}
                    onChange={e => setQty(e.target.value)} className="h-10 bg-white" />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Description (optional)</label>
                  <textarea
                    placeholder="Item Description (shown on estimate/invoice)"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={2}
                    className="w-full text-sm border rounded-md p-2 text-gray-700 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>



                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Before</p>
                    {photoPreview ? (
                      <div className="relative">
                        <img src={photoPreview} alt="Before" className="w-full h-24 object-cover rounded-md" />
                        <button type="button" onClick={() => setPhotoPreview(null)}
                          className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => photoInputRef.current?.click()}
                        className="w-full h-24 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-green-300 rounded-md text-green-700 text-xs hover:bg-green-100">
                        <Camera className="w-4 h-4" />
                        Before
                      </button>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">After</p>
                    {afterPhotoPreview ? (
                      <div className="relative">
                        <img src={afterPhotoPreview} alt="After" className="w-full h-24 object-cover rounded-md" />
                        <button type="button" onClick={() => setAfterPhotoPreview(null)}
                          className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => afterPhotoInputRef.current?.click()}
                        className="w-full h-24 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-blue-300 rounded-md text-blue-600 text-xs hover:bg-blue-50">
                        <Camera className="w-4 h-4" />
                        After
                      </button>
                    )}
                  </div>
                </div>

                <Button onClick={addItem} className="w-full bg-green-600 hover:bg-green-700 h-10">
                  <Plus className="w-4 h-4 mr-1" /> Add Item to Job
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Items on Job ({visible.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {visible.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No items yet</p>
            ) : (
              <div className="space-y-2">
                {visible.map((item) => (
                  <div key={item._cid} data-testid="item-row" className="flex flex-col py-3 border-b last:border-0 gap-2">
                    <div className="flex items-center justify-between">
                      {switchingItemIdx === item._cid ? (
                        <div className="flex-1 mr-2 relative">
                          <div className="flex gap-2">
                            <Input type="text" placeholder="Search item..." value={subSearchQuery}
                              onChange={(e) => setSubSearchQuery(e.target.value)} className="h-8 text-xs bg-white" autoFocus />
                            <Button size="sm" variant="ghost"
                              onClick={() => { setSwitchingItemIdx(null); setSubSearchQuery(''); setSubSearchResults([]); }}
                              className="h-8 text-xs shrink-0">Cancel</Button>
                          </div>
                          {subSearchQuery && (
                            <div className="absolute left-0 right-0 z-50 mt-1 max-h-40 overflow-y-auto bg-white border rounded-md shadow-lg p-1 text-xs">
                              {subSearching && <p className="p-2 text-gray-400">Searching...</p>}
                              {!subSearching && subSearchResults.length === 0 && <p className="p-2 text-gray-400">No items found</p>}
                              {subSearchResults.map(resItem => (
                                <div key={resItem.name} onClick={() => switchItemForLine(item._cid, resItem)}
                                  className="p-2 hover:bg-gray-100 cursor-pointer rounded border-b last:border-0 font-medium text-gray-700">
                                  {resItem.item_name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900 text-sm">{item.item_name}</p>
                          {!isSigned && (
                            <Button size="sm" variant="link"
                              onClick={() => { setSwitchingItemIdx(item._cid); setSubSearchQuery(''); setSubSearchResults([]); }}
                              className="h-auto p-0 text-xs text-blue-600 hover:text-blue-800">Change Item</Button>
                          )}
                          <SavedBadge row={item} onRetry={() => syncRow(item._cid)} />
                        </div>
                      )}
                      {!isSigned && switchingItemIdx !== item._cid && (
                        <button onClick={() => removeItem(item._cid)} aria-label="Remove item" className="text-red-400 hover:text-red-600 p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {item._sync === 'error' && <p className="text-[11px] text-red-600">{item._err}</p>}

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] font-medium text-gray-500 uppercase">Zone</label>
                        <Input type="text" placeholder="Zone" value={item.custom_zone || ''} disabled={isSigned}
                          onChange={(e) => editRow(item._cid, { custom_zone: e.target.value })} className="h-8 text-xs bg-white" />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-gray-500 uppercase">Qty</label>
                        <Input type="number" min="0.01" step="any" value={item.qty} disabled={isSigned}
                          onChange={(e) => editRow(item._cid, { qty: parseFloat(e.target.value) || 0 })} className="h-8 text-xs bg-white" />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-gray-500 uppercase">Rate ($)</label>
                        <Input type="number" min="0" step="0.01" value={item.rate} disabled={isSigned}
                          onChange={(e) => editRow(item._cid, { rate: parseFloat(e.target.value) || 0 })} className="h-8 text-xs bg-white" />
                      </div>
                    </div>

                    <div className="flex items-start justify-between gap-3 mt-1">
                      <div className="flex-1">
                        <textarea value={item.description} disabled={isSigned}
                          onChange={(e) => editRow(item._cid, { description: e.target.value })}
                          placeholder="Description (shown on estimate/invoice)" rows={2}
                          className="w-full text-xs border rounded-md p-1.5 text-gray-700 resize-none focus:outline-none focus:ring-1 focus:ring-green-500" />
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <div className="flex gap-2">
                          {['before', 'after'].map(type => (
                            <PhotoSlot key={type} row={item} type={type} isSigned={isSigned}
                              uploading={uploadingKey === `${item._cid}:${type}` || (!!item._photos?.[type] && item._sync === 'saving')}
                              onPick={() => openPhotoFor(item._cid, type)}
                              onRemove={() => handleRemovePhoto(item._cid, type)} />
                          ))}
                        </div>
                        <p className="text-xs font-bold text-gray-900 mt-0.5">${Number(item.amount).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))}
                <p className="text-sm font-bold text-gray-900 text-right pt-2">Total: ${total.toFixed(2)}</p>
              </div>
            )}
          </CardContent>
        </Card>
        <AppVersion />
      </div>

      {!isSigned && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
          <div data-testid="sync-status" className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold">
              {errorCount > 0 ? (
                <span className="text-red-600">{errorCount} item change{errorCount > 1 ? 's' : ''} NOT saved — kept on this device</span>
              ) : unsaved.length > 0 ? (
                <span className="text-amber-600">Saving…</span>
              ) : (
                <span className="text-green-700 flex items-center gap-1"><Check className="w-4 h-4" /> All changes saved to job</span>
              )}
            </div>
            {errorCount > 0 ? (
              <Button onClick={retryAll} className="bg-red-600 hover:bg-red-700 text-white h-10">
                <RefreshCw className="w-4 h-4 mr-1" /> Retry
              </Button>
            ) : (
              <Button onClick={() => navigate(backPath)} disabled={unsaved.length > 0} className="bg-green-600 hover:bg-green-700 text-white h-10">Done</Button>
            )}
          </div>
        </div>
      )}

      {/* Chat with Office dialog */}
      {chatOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-4 space-y-3">
            <h3 className="font-bold text-gray-900">Message Office</h3>
            <p className="text-xs text-gray-500">Job: {jobId}</p>
            <textarea
              className="w-full border rounded-md p-2 text-sm resize-none"
              rows={4}
              placeholder="e.g. Need price for item XYZ, or please remove item from job…"
              value={chatText}
              onChange={e => setChatText(e.target.value)}
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setChatOpen(false)}>Cancel</Button>
              <Button
                disabled={chatSending || !chatText.trim()}
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={async () => {
                  setChatSending(true);
                  try {
                    const techName = localStorage.getItem('technicianName') || 'Tech';
                    await crmBackend.sendChatMessage(chatText.trim(), techName, jobId);
                    toast({ title: 'Message sent to office' });
                    setChatOpen(false);
                  } catch (err) {
                    toast({ title: 'Send failed', description: err.message, variant: 'destructive' });
                  } finally {
                    setChatSending(false);
                  }
                }}
              >
                <MessageCircle className="w-4 h-4 mr-1" />
                {chatSending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file inputs for photo capture (always present in DOM) */}
      <input ref={photoInputRef} type="file" accept="image/*" capture="environment"
        className="hidden" onChange={handlePhotoCapture} />
      <input ref={afterPhotoInputRef} type="file" accept="image/*" capture="environment"
        className="hidden" onChange={e => {
          const file = e.target.files?.[0];
          capturePhoto(file, setAfterPhotoPreview);
          e.target.value = '';
        }} />
      <input ref={linePhotoInputRef} type="file" accept="image/*" capture="environment"
        className="hidden" onChange={handleLinePhotoCapture} />
    </div>
  );
};

export default TechAddItems;